import React from 'react';
import { useBlocker } from 'react-router-dom';
import ShiftRosterTab from '../roster/ShiftRosterTab';
import LoginOutlined from '@mui/icons-material/LoginOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import EventBusyOutlined from '@mui/icons-material/EventBusyOutlined';
import {
  Modal,
  ConfirmDialog,
  Button,
  Badge,
  Input,
  Select,
  Textarea,
  Alert,
  DataTable,
  StatTile,
  PageHeader,
} from '../../../components/ui';

import {
  calculateOvertimeMs, calculateWorkedMs, formatAbsenceType, formatDuration,
  formatReadableSystemRemark, formatTime, inferAssignedShift, normalizeAssignedShift,
  OVERTIME_REMARKS_THRESHOLD_MS, SHIFT_OPTIONS,
} from '../utils/attendanceUtils';

const AttendanceContent = ({
  role,
  loading,
  actionLoading,
  error,
  remarks,
  onRemarksChange,
  absenceType,
  absenceReason,
  vacationStartDate,
  vacationEndDate,
  onAbsenceTypeChange,
  onAbsenceReasonChange,
  onVacationStartDateChange,
  onVacationEndDateChange,
  onReportAbsence,
  missedCheckoutRequests,
  missedCheckoutAttendanceId,
  missedCheckoutTime,
  missedCheckoutReason,
  onMissedCheckoutAttendanceIdChange,
  onMissedCheckoutTimeChange,
  onMissedCheckoutReasonChange,
  onSubmitMissedCheckoutRequest,
  onApproveMissedCheckoutRequest,
  onRejectMissedCheckoutRequest,
  onResolveVisibleFlags,
  attendanceStateLabel,
  todayRecord,
  history,
  adminUsernameFilter,
  onAdminFilterChange,
  showFlaggedOnly,
  onToggleFlaggedOnly,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  page,
  totalPages,
  onPrevPage,
  onNextPage,
  onApplyAdminFilter,
  onClearFilters,
  onApplyQuickRange,
  editingRecordId,
  editStatus,
  editAssignedShift,
  editRemarks,
  editCheckInTime,
  editCheckOutTime,
  onEditStatusChange,
  onEditAssignedShiftChange,
  onEditRemarksChange,
  onEditCheckInTimeChange,
  onEditCheckOutTimeChange,
  activeRemark,
  onOpenRemarkViewer,
  onCloseRemarkViewer,
  onAdminEditStart,
  onAdminEditCancel,
  onAdminEditSave,
  onAdminDelete,
  onExportCsv,
  onExportPageCsv,
  onRefresh,
  onCheckIn,
  onCheckOut,
}) => {
  const [isAbsenceModalOpen, setIsAbsenceModalOpen] = React.useState(false);

  const [activeTab, setActiveTab] = React.useState('attendance');
  const [rosterVisited, setRosterVisited] = React.useState(false);
  const [rosterDirty, setRosterDirty] = React.useState(false);
  const [rosterBusy, setRosterBusy] = React.useState(false);
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    (rosterDirty || rosterBusy) && currentLocation.pathname !== nextLocation.pathname);
  React.useEffect(() => {
    if (!rosterDirty && !rosterBusy) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [rosterDirty, rosterBusy]);

  const isAdmin = role === 'admin';
  const isCheckedIn = todayRecord?.status === 'checked-in';
  const isCheckedOut = todayRecord?.status === 'checked-out';
  const isAbsentToday = todayRecord?.attendanceKind === 'absence';
  const hasTodayRecord = Boolean(todayRecord);
  const busy = loading || actionLoading;
  const isVacationSelection = absenceType === 'vacation';
  const absenceActionDisabled = busy || isCheckedIn || (!isVacationSelection && hasTodayRecord && !isAbsentToday);
  const liveOvertimeMs = isCheckedIn
    ? calculateOvertimeMs(todayRecord?.checkInTime, new Date(), todayRecord?.assignedShift)
    : 0;
  const remarksRequired = liveOvertimeMs > OVERTIME_REMARKS_THRESHOLD_MS && !remarks.trim();
  const todayWorkedMs = calculateWorkedMs(todayRecord?.checkInTime, todayRecord?.checkOutTime);
  const todayOvertimeMs = calculateOvertimeMs(
    todayRecord?.checkInTime,
    todayRecord?.checkOutTime,
    todayRecord?.assignedShift
  );
  const editRecord = history.find((item) => item._id === editingRecordId) || null;
  const openRecords = history.filter((item) => item.status === 'checked-in' && !item.checkOutTime);
  const editWorkedMs = calculateWorkedMs(editCheckInTime, editCheckOutTime);
  const editOvertimeMs = calculateOvertimeMs(editCheckInTime, editCheckOutTime, editAssignedShift);
  const editRemarksRequired = editOvertimeMs > OVERTIME_REMARKS_THRESHOLD_MS && !editRemarks.trim();

  const columns = [
    ...(isAdmin ? [{ key: 'username', header: 'User', render: (r) => r.username || '—' }] : []),
    {
      key: 'date',
      header: 'Date',
      primary: true,
      render: (r) => new Date(r.date).toLocaleDateString('en-GB'),
    },
    {
      key: 'checkInTime',
      header: 'Check in',
      render: (r) => <span className="tabular">{formatTime(r.checkInTime)}</span>,
    },
    {
      key: 'checkOutTime',
      header: 'Check out',
      render: (r) => <span className="tabular">{formatTime(r.checkOutTime)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => {
        if (r.attendanceKind === 'absence') {
          return (
            <Badge variant="warning" size="sm" dot>
              {formatAbsenceType(r.absenceType)}
            </Badge>
          );
        }

        return (
          <Badge variant={r.status === 'checked-out' ? 'success' : 'info'} size="sm" dot>
            {r.status === 'checked-out' ? 'Checked out' : 'Checked in'}
          </Badge>
        );
      },
    },
    {
      key: 'assignedShift',
      header: 'Shift',
      render: (r) => {
        if (r.attendanceKind === 'absence') return '—';
        const shiftKey = inferAssignedShift(r.checkInTime) || normalizeAssignedShift(r.assignedShift);
        if (shiftKey === 'morning') return 'Morning';
        if (shiftKey === 'afternoon') return 'Afternoon';
        if (shiftKey === 'night') return 'Night';
        return '—';
      },
    },
    {
      key: 'overtime',
      header: 'Overtime',
      render: (r) => {
        if (r.attendanceKind === 'absence') return '—';
        return <span className="tabular">{formatDuration(calculateOvertimeMs(r.checkInTime, r.checkOutTime, r.assignedShift))}</span>;
      },
    },
    {
      key: 'review',
      header: 'Review',
      render: (r) => (
        r.flaggedForReview
          ? <Badge variant="warning" size="sm">Flagged</Badge>
          : <Badge variant="neutral" size="sm">OK</Badge>
      ),
    },
    {
      key: 'remarks',
      header: 'Remarks',
      render: (r) => {
        if (!r.remarks) return '—';
        return (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onOpenRemarkViewer(formatReadableSystemRemark(r.remarks))}
            disabled={actionLoading}
          >
            View
          </Button>
        );
      },
    },
    ...(isAdmin
      ? [
          {
            key: 'actions',
            header: 'Actions',
            render: (r) => (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  icon={EditOutlined}
                  onClick={() => onAdminEditStart(r)}
                  disabled={actionLoading}
                >
                  Edit
                </Button>
                <Button
                  variant="danger-soft"
                  size="sm"
                  icon={DeleteOutline}
                  onClick={() => onAdminDelete(r._id)}
                  disabled={actionLoading}
                >
                  Delete
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  const handleSubmitAbsence = async () => {
    const success = await onReportAbsence();
    if (success) {
      setIsAbsenceModalOpen(false);
    }
  };

  return (
    <>
      <PageHeader
        className="!bg-transparent"
        title="Attendance"
        backTo="/"
        subtitle={activeTab === 'roster' ? 'Monthly shift assignments' : 'Mark attendance and review recent records'}
        actions={activeTab === 'attendance' && <Button variant="secondary" icon={RefreshOutlined} onClick={onRefresh} disabled={busy}>
              Refresh
            </Button>}
      />
        <div role="tablist" aria-label="Attendance sections" className="mb-5 mt-5 flex gap-2 border-b border-border pb-3">
          {['attendance', 'roster'].map((tab) => <Button key={tab} role="tab" id={`attendance-tab-${tab}`} aria-selected={activeTab === tab}
            aria-controls={`attendance-panel-${tab}`} variant={activeTab === tab ? 'primary' : 'ghost'}
            onClick={() => { setActiveTab(tab); if (tab === 'roster') setRosterVisited(true); }}>
            {tab === 'attendance' ? 'Attendance' : 'Shift roster'}
          </Button>)}
        </div>
        <div role="tabpanel" id="attendance-panel-attendance" aria-labelledby="attendance-tab-attendance" hidden={activeTab !== 'attendance'}>
        <div className="space-y-6">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile
            label="Today"
            value={attendanceStateLabel}
            accent={isAbsentToday ? 'warning' : isCheckedOut ? 'success' : isCheckedIn ? 'info' : 'neutral'}
          />
          <StatTile label="Check in" value={formatTime(todayRecord?.checkInTime)} />
          <StatTile label="Check out" value={formatTime(todayRecord?.checkOutTime)} />
          <StatTile
            label="Overtime"
            value={formatDuration(todayOvertimeMs)}
            hint={`Worked: ${formatDuration(todayWorkedMs)}`}
          />
        </div>

        <div>
          <Textarea
            label="Remarks"
              hint={remarksRequired
                ? 'Overtime exceeded 1 hour. Please add remarks before checking out.'
              : "Optional note attached to today's attendance."}
            rows={3}
            value={remarks}
            onChange={(e) => onRemarksChange(e.target.value)}
            placeholder="Add a note for today"
          />

            {remarksRequired && (
            <Alert variant="warning" className="mt-3">
                Remarks are required when overtime exceeds 1 hour (more than 9 hours worked).
            </Alert>
          )}

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="success"
                icon={LoginOutlined}
                onClick={onCheckIn}
                loading={actionLoading}
                disabled={busy || isCheckedIn || isCheckedOut}
              >
                Check in
              </Button>
              <Button
                icon={LogoutOutlined}
                onClick={onCheckOut}
                loading={actionLoading}
                  disabled={busy || !isCheckedIn || isCheckedOut || remarksRequired}
              >
                Check out
              </Button>
            </div>
            <Button
              variant="secondary"
              icon={EventBusyOutlined}
              onClick={() => setIsAbsenceModalOpen(true)}
              disabled={busy}
            >
              Report absence
            </Button>
          </div>
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        <section>
          <h3 className="mb-3 text-sm font-semibold text-foreground">History</h3>

          <div className="mb-4 rounded-lg border border-border bg-muted/40 p-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Input
                label="From"
                type="date"
                value={startDate}
                onChange={(e) => onStartDateChange(e.target.value)}
              />
              <Input
                label="To"
                type="date"
                value={endDate}
                onChange={(e) => onEndDateChange(e.target.value)}
              />
              {isAdmin && (
                <Input
                  label="Username"
                  type="search"
                  value={adminUsernameFilter}
                  onChange={(e) => onAdminFilterChange(e.target.value)}
                  placeholder="Filter by user"
                />
              )}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={onApplyAdminFilter} disabled={busy}>
                Apply
              </Button>
              <Button variant="ghost" size="sm" onClick={onClearFilters} disabled={busy}>
                Clear
              </Button>
              {isAdmin && (
                <Button variant={showFlaggedOnly ? 'accent' : 'secondary'} size="sm" onClick={onToggleFlaggedOnly} disabled={busy}>
                  {showFlaggedOnly ? 'Showing flagged' : 'Show flagged only'}
                </Button>
              )}
              {isAdmin && showFlaggedOnly && (
                <Button variant="success" size="sm" onClick={onResolveVisibleFlags} disabled={busy}>
                  Resolve visible flags
                </Button>
              )}

              {/* Quick ranges are secondary to the explicit date fields above,
                  so they read as one group of equal-weight shortcuts. */}
              <span className="mx-1 hidden h-5 w-px bg-border sm:block" aria-hidden="true" />
              {[
                { id: 'today', label: 'Today' },
                { id: 'last7', label: 'Last 7 days' },
                { id: 'month', label: 'This month' },
              ].map(({ id, label }) => (
                <Button
                  key={id}
                  variant="secondary"
                  size="sm"
                  onClick={() => onApplyQuickRange(id)}
                  disabled={busy}
                >
                  {label}
                </Button>
              ))}

                <>
                  <span className="mx-1 hidden h-5 w-px bg-border sm:block" aria-hidden="true" />
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={DownloadOutlined}
                    onClick={onExportCsv}
                    disabled={busy}
                  >
                    All CSV
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={DownloadOutlined}
                    onClick={onExportPageCsv}
                    disabled={busy}
                  >
                    Page CSV
                  </Button>
                </>
            </div>
          </div>

          <DataTable
            columns={columns}
            rows={history}
            loading={loading}
            emptyTitle="No attendance records"
            emptyDescription="Nothing matches the current filters."
          />

          {totalPages > 1 && (
            <nav
              aria-label="Attendance pagination"
              className="mt-4 flex items-center justify-between gap-3"
            >
              <span className="tabular text-sm text-muted-fg">
                Page {page} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  icon={ChevronLeft}
                  onClick={onPrevPage}
                  disabled={busy || page <= 1}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  iconRight={ChevronRight}
                  onClick={onNextPage}
                  disabled={busy || page >= totalPages}
                >
                  Next
                </Button>
              </div>
            </nav>
          )}
        </section>

        {!isAdmin && (
          <section className="rounded-lg border border-border bg-muted/30 p-4">
            <h3 className="text-sm font-semibold text-foreground">Missed checkout request</h3>
            <p className="mt-1 text-sm text-muted-fg">
              Forgot to check out? Submit a request and admin can approve the correction.
            </p>

            {openRecords.length === 0 ? (
              <Alert variant="info" className="mt-3">No open attendance records found in current history.</Alert>
            ) : (
              <div className="mt-3 space-y-3">
                <Select
                  label="Attendance record"
                  value={missedCheckoutAttendanceId}
                  onChange={(e) => onMissedCheckoutAttendanceIdChange(e.target.value)}
                >
                  <option value="">Select record</option>
                  {openRecords.map((record) => (
                    <option key={record._id} value={record._id}>
                      {new Date(record.date).toLocaleDateString('en-GB')} - Check in {formatTime(record.checkInTime)}
                    </option>
                  ))}
                </Select>

                <Input
                  label="Requested check-out time"
                  type="datetime-local"
                  value={missedCheckoutTime}
                  onChange={(e) => onMissedCheckoutTimeChange(e.target.value)}
                />

                <Textarea
                  label="Reason"
                  rows={3}
                  value={missedCheckoutReason}
                  onChange={(e) => onMissedCheckoutReasonChange(e.target.value)}
                  placeholder="Explain why checkout was missed"
                />

                <Button
                  size="sm"
                  onClick={onSubmitMissedCheckoutRequest}
                  loading={actionLoading}
                  disabled={busy}
                >
                  Submit request
                </Button>
              </div>
            )}
          </section>
        )}

        {isAdmin && missedCheckoutRequests.length > 0 && (
          <section className="rounded-lg border border-border bg-muted/30 p-4">
            <h3 className="text-sm font-semibold text-foreground">Pending missed-checkout requests</h3>
            <div className="mt-3 space-y-2">
              {missedCheckoutRequests.map((request) => (
                <div key={request._id} className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface p-3">
                  <div className="min-w-0 flex-1 text-sm text-foreground">
                    <span className="font-semibold">{request.username}</span>
                    <span className="text-muted-fg"> requested </span>
                    <span>{new Date(request.requestedCheckOutTime).toLocaleString('en-GB')}</span>
                    <span className="text-muted-fg"> - {request.reason}</span>
                  </div>
                  <Button size="sm" variant="success" onClick={() => onApproveMissedCheckoutRequest(request._id)} disabled={busy}>
                    Approve
                  </Button>
                  <Button size="sm" variant="danger-soft" onClick={() => onRejectMissedCheckoutRequest(request._id)} disabled={busy}>
                    Reject
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}
        </div>
        </div>
        <div role="tabpanel" id="attendance-panel-roster" aria-labelledby="attendance-tab-roster" hidden={activeTab !== 'roster'}>
          {rosterVisited && <ShiftRosterTab role={role} onDirtyChange={setRosterDirty} onBusyChange={setRosterBusy} />}
        </div>
      <ConfirmDialog isOpen={blocker.state === 'blocked'} onClose={() => blocker.reset?.()}
        title={rosterBusy ? 'Roster operation in progress' : 'Discard roster changes?'}
        message={rosterBusy ? 'Wait for the roster operation to finish before leaving this page.' : 'Your unsaved roster or employee setup changes will be lost.'}
        confirmLabel="Discard and leave" loading={rosterBusy}
        onConfirm={() => { if (!rosterBusy) blocker.proceed?.(); }} />

      <Modal
        isOpen={Boolean(activeRemark)}
        onClose={onCloseRemarkViewer}
        size="md"
        title="Attendance remarks"
        footer={
          <Button variant="ghost" onClick={onCloseRemarkViewer}>
            Close
          </Button>
        }
      >
        <div className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
          {activeRemark}
        </div>
      </Modal>

      <Modal
        isOpen={isAbsenceModalOpen}
        onClose={() => setIsAbsenceModalOpen(false)}
        size="md"
        title="Report absence"
        subtitle="Submit sick leave or vacation timeline"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsAbsenceModalOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button variant="warning" onClick={handleSubmitAbsence} loading={actionLoading} disabled={absenceActionDisabled}>
              Submit report
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {isCheckedIn && (
            <Alert variant="warning">
              You are currently checked in. Please check out before reporting absence.
            </Alert>
          )}

          {hasTodayRecord && !isAbsentToday && !isCheckedIn && (
            <Alert variant="info">
              Today's attendance has already been recorded. You can still submit a vacation timeline for upcoming dates.
            </Alert>
          )}

          <Select
            label="Absence type"
            value={absenceType}
            onChange={(e) => onAbsenceTypeChange(e.target.value)}
          >
            <option value="sick-leave">Sick leave</option>
            <option value="vacation">Vacation</option>
          </Select>

          {isVacationSelection && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label="Vacation start"
                type="date"
                value={vacationStartDate}
                onChange={(e) => onVacationStartDateChange(e.target.value)}
              />
              <Input
                label="Vacation end"
                type="date"
                value={vacationEndDate}
                min={vacationStartDate || undefined}
                onChange={(e) => onVacationEndDateChange(e.target.value)}
              />
            </div>
          )}

          <Textarea
            label="Reason"
            rows={3}
            value={absenceReason}
            onChange={(e) => onAbsenceReasonChange(e.target.value)}
            placeholder="Provide a short reason"
          />
        </div>
      </Modal>

      {isAdmin && (
        <Modal
          isOpen={Boolean(editingRecordId)}
          onClose={onAdminEditCancel}
          size="md"
          title="Edit attendance"
          subtitle={editRecord ? `${editRecord.username || 'User'} · ${new Date(editRecord.date).toLocaleDateString('en-GB')}` : undefined}
          footer={
            <>
              <Button variant="ghost" onClick={onAdminEditCancel} disabled={actionLoading}>
                Cancel
              </Button>
              <Button variant="success" onClick={onAdminEditSave} loading={actionLoading}>
                Save changes
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {editRemarksRequired && (
              <Alert variant="warning">
                Remarks are required when overtime exceeds 1 hour (more than 9 hours worked).
              </Alert>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Check in"
                type="datetime-local"
                value={editCheckInTime}
                max={editCheckOutTime || undefined}
                onChange={(e) => onEditCheckInTimeChange(e.target.value)}
              />
              <Input
                label="Check out"
                type="datetime-local"
                value={editCheckOutTime}
                min={editCheckInTime || undefined}
                onChange={(e) => onEditCheckOutTimeChange(e.target.value)}
              />
            </div>

            <Select
              label="Status"
              value={editStatus}
              onChange={(e) => onEditStatusChange(e.target.value)}
            >
              <option value="checked-in">Checked in</option>
              <option value="checked-out">Checked out</option>
            </Select>

            <Select
              label="Assigned shift"
              value={editAssignedShift}
              onChange={(e) => onEditAssignedShiftChange(e.target.value)}
            >
              <option value="">Select shift</option>
              {SHIFT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            <Input
              label="Remarks"
              value={editRemarks}
              onChange={(e) => onEditRemarksChange(e.target.value)}
              placeholder="Add remarks"
              hint={editRemarksRequired ? 'Required for overtime beyond 1 hour.' : 'Optional'}
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <StatTile label="Worked" value={formatDuration(editWorkedMs)} />
              <StatTile label="Overtime" value={formatDuration(editOvertimeMs)} />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default AttendanceContent;